import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ArrowUpRight } from 'lucide-react';
import { companyInfo } from '../mockData';

const navLinks = [
  { name: 'Accueil', path: '/', id: 'home' },
  { name: 'TSE OUEST', path: '/about', id: 'about' },
  { name: 'Nos Services', path: '/services', id: 'services' },
  { name: 'Nos Activités', path: '/activities', id: 'activities' },
  { name: 'Actualités', path: '/blog', id: 'blog' },
  { name: 'Galerie Photo', path: '/portfolio', id: 'portfolio' }
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 px-3 sm:px-6 pt-3 sm:pt-4" data-testid="navbar">
      <nav
        className={`max-w-7xl mx-auto rounded-2xl border transition-all duration-500 ${
          isScrolled
            ? 'bg-abyss/85 backdrop-blur-2xl border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)]'
            : 'bg-abyss/60 backdrop-blur-xl border-white/5'
        }`}
      >
        <div className="flex items-center justify-between h-[72px] px-4 sm:px-5">
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <div className="bg-white rounded-xl px-2.5 py-1.5 shadow-[0_0_0_1px_rgba(255,255,255,0.15)] transition-shadow duration-300 group-hover:shadow-[0_0_24px_rgba(255,94,20,0.45)]">
              <img src="/assets/tse-ouest-logo-transparent.png" alt="TSE OUEST" className="h-10 w-auto" />
            </div>
            <div className="hidden xl:block leading-tight">
              <p className="font-display font-extrabold text-white tracking-tight">TSE OUEST</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Angers · 49</p>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-0.5 xl:gap-1">
            {navLinks.map((l) => {
              const active = location.pathname === l.path;
              return (
                <Link
                  key={l.path}
                  to={l.path}
                  data-testid={`nav-link-${l.id}`}
                  className={`relative whitespace-nowrap text-sm font-medium px-2.5 xl:px-3 py-2 rounded-lg transition-colors duration-200 ${
                    active ? 'text-white' : 'text-slate-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {l.name}
                  {active && (
                    <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 rounded-full bg-energy shadow-[0_0_8px_#FF5E14]" />
                  )}
                </Link>
              );
            })}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`tel:${companyInfo.phone}`}
              className="hidden xl:flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-signal transition-colors"
              data-testid="nav-phone-link"
            >
              <Phone size={16} />
              <span className="font-mono">06 99 13 45 11</span>
            </a>
            <Link
              to="/contact"
              data-testid="nav-cta-rendezvous"
              className="btn-energy inline-flex whitespace-nowrap items-center gap-2 text-sm font-bold text-white px-4 xl:px-5 py-2.5 rounded-xl"
            >
              Prenez rendez-vous
              <ArrowUpRight size={16} />
            </Link>
          </div>

          <button
            className="lg:hidden text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-white/10 px-4 py-4 space-y-1" data-testid="nav-mobile-menu">
            {navLinks.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                data-testid={`nav-mobile-link-${l.id}`}
                className={`block px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                  location.pathname === l.path ? 'text-energy bg-white/5' : 'text-slate-200 hover:bg-white/5'
                }`}
              >
                {l.name}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="nav-mobile-cta"
              className="btn-energy mt-3 flex items-center justify-center gap-2 font-bold text-white px-5 py-3 rounded-xl"
            >
              Prenez rendez-vous <ArrowUpRight size={16} />
            </Link>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
